import React, { useMemo, useState, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getGroupedRowModel,
  getExpandedRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
  type ColumnDef,
  type GroupingState,
} from '@tanstack/react-table';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { LineChart, X, ChevronRight, ChevronDown, GripVertical, ExternalLink, Pencil } from 'lucide-react';
import { useAdmin } from '../../hooks/useAdmin';
import {
  COLUMN_GROUPS,
  getOrderedGroups,
  getVisibleColumnsInGroup,
  isFirstColumnInGroup,
  buildOrderedColumnList,
  getColumnGroup,
  type ColumnGroup,
} from './columnGroups';

export interface TickerResult {
  ticker: string;
  totalReturn: number;
  cagr: number;
  finalValue: number;
  dividendsReceived: number;
  // Enhanced metrics
  inceptionDate?: string | null;
  finalValue0Percent?: number | null;
  finalValue50Percent?: number | null;
  finalValue100Percent?: number | null;
  flatThresholdPercent?: number | null;
  dividendYield?: number | null;
  maxDrawdownPercent?: number | null;
  volatilityPercent?: number | null;
  dividendGrowthRatePercent?: number | null;
  underlyingTicker?: string | null;
  // Share price fields
  startPrice?: number | null;
  endPrice?: number | null;
  priceChangeDollars?: number | null;
  priceChangePercent?: number | null;
  // Metadata for grouping (Show By page)
  name?: string | null;
  sector?: string | null;
  industry?: string | null;
  assetType?: 'EQUITY' | 'ETF' | null;
}

export interface ChartDataPoint {
  ticker: string;
  dates: string[];
  values: number[];
  valuesWithoutDrip?: number[] | null;
}

// Loading ticker type for optimistic UI
export interface LoadingTicker {
  symbol: string;
  startedAt: number;
}

export interface ReturnsTableProps {
  data: TickerResult[];
  className?: string;
  onRowClick?: (ticker: string) => void;
  chartData?: ChartDataPoint[];
  visibleColumns?: string[];
  onRemoveTicker?: (ticker: string) => void;
  loadingTickers?: LoadingTicker[];
  groupByUnderlying?: boolean;
  // Column group ordering
  groupOrder?: string[];
  onReorderGroups?: (activeId: string, overId: string) => void;
  // Admin: Called after underlying ticker is updated (for data refresh)
  onUnderlyingUpdated?: (symbol: string, newUnderlying: string | null) => void;
}

// Format number as currency (no decimals for cleaner display)
const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Format number as percentage (1 decimal place)
const formatPercent = (value: number | null | undefined, showSign = true): string => {
  if (value == null) return '-';
  const sign = showSign && value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

// Format date
const formatDate = (value: string | null | undefined): string => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Helper to render cell values based on column type (for group header rows)
const renderCellValue = (columnId: string, value: any): React.ReactNode => {
  if (value == null) return <span className="text-muted-foreground">-</span>;

  // Percentage columns with color coding
  if (['totalReturn', 'cagr', 'dividendGrowthRatePercent', 'priceChangePercent'].includes(columnId)) {
    const colorClass = value >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
    return <span className={`font-medium ${colorClass}`}>{formatPercent(value)}</span>;
  }

  // Max drawdown (always negative/red)
  if (columnId === 'maxDrawdownPercent') {
    return (
      <span className="text-red-600 dark:text-red-400">
        {value != null ? `-${Math.abs(value).toFixed(1)}%` : '-'}
      </span>
    );
  }

  // Percentage columns without sign
  if (['dividendYield', 'volatilityPercent', 'flatThresholdPercent'].includes(columnId)) {
    return <span className="text-muted-foreground">{formatPercent(value, false)}</span>;
  }

  // Currency columns
  if (['finalValue', 'finalValue0Percent', 'finalValue50Percent', 'finalValue100Percent',
       'dividendsReceived', 'startPrice', 'endPrice'].includes(columnId)) {
    return <span className="text-muted-foreground">{formatCurrency(value)}</span>;
  }

  // Price change dollars (with color)
  if (columnId === 'priceChangeDollars') {
    const colorClass = value >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
    const sign = value >= 0 ? '+' : '';
    return <span className={colorClass}>{sign}{formatCurrency(value)}</span>;
  }

  // Date columns
  if (columnId === 'inceptionDate') {
    return <span className="text-muted-foreground">{formatDate(value)}</span>;
  }

  // Underlying ticker
  if (columnId === 'underlyingTicker') {
    return <span className="text-muted-foreground font-mono">{value || '-'}</span>;
  }

  // Default - just display the value
  return <span className="text-muted-foreground">{String(value)}</span>;
};

const columnHelper = createColumnHelper<TickerResult>();

// Define all available columns with their IDs (for backwards compatibility)
export const COLUMN_DEFINITIONS = [
  // Core columns
  { id: 'ticker', label: 'Ticker', group: 'identity' },
  { id: 'underlyingTicker', label: 'Underlying', group: 'identity' },
  { id: 'inceptionDate', label: 'Inception Date', group: 'identity' },
  // Performance columns
  { id: 'totalReturn', label: 'Total Return', group: 'performance' },
  { id: 'cagr', label: 'CAGR', group: 'performance' },
  // DRIP Scenario columns
  { id: 'finalValue0Percent', label: 'Final (0% DRIP)', group: 'drip' },
  { id: 'finalValue50Percent', label: 'Final (50% DRIP)', group: 'drip' },
  { id: 'finalValue', label: 'Final (100% DRIP)', group: 'drip' },
  { id: 'flatThresholdPercent', label: 'Flat Threshold', group: 'drip' },
  // Dividend columns
  { id: 'dividendYield', label: 'Div Yield', group: 'dividends' },
  { id: 'dividendsReceived', label: 'Dividends', group: 'dividends' },
  { id: 'dividendGrowthRatePercent', label: 'Div Growth', group: 'dividends' },
  // Risk columns
  { id: 'maxDrawdownPercent', label: 'Max Drawdown', group: 'risk' },
  { id: 'volatilityPercent', label: 'Volatility', group: 'risk' },
  // Share price columns
  { id: 'startPrice', label: 'Start Price', group: 'price' },
  { id: 'endPrice', label: 'End Price', group: 'price' },
  { id: 'priceChangeDollars', label: 'Price Δ ($)', group: 'price' },
  { id: 'priceChangePercent', label: 'Price Δ (%)', group: 'price' },
];

export const DEFAULT_VISIBLE_COLUMNS = [
  'ticker',
  'underlyingTicker',
  'totalReturn',
  'cagr',
  'finalValue',
  'dividendsReceived',
];

// Draggable group header component
interface DraggableGroupHeaderProps {
  group: ColumnGroup;
  colSpan: number;
  isFirst: boolean;
}

function DraggableGroupHeader({ group, colSpan, isFirst }: DraggableGroupHeaderProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <th
      ref={setNodeRef}
      style={style}
      colSpan={colSpan}
      className={`px-2 py-2 text-center text-xs font-bold uppercase tracking-wider text-foreground truncate
        ${group.colorClass}
        ${!isFirst ? 'border-l-2 border-slate-500' : ''}
        cursor-grab active:cursor-grabbing
      `}
      {...attributes}
      {...listeners}
    >
      <div className="flex items-center justify-center gap-1">
        <GripVertical className="h-3 w-3 text-muted-foreground opacity-50 flex-shrink-0" />
        <span className="truncate">{group.label}</span>
      </div>
    </th>
  );
}

export const ReturnsTable: React.FC<ReturnsTableProps> = ({
  data,
  className = '',
  onRowClick,
  chartData: _chartData,
  visibleColumns = DEFAULT_VISIBLE_COLUMNS,
  onRemoveTicker,
  loadingTickers = [],
  groupByUnderlying = false,
  groupOrder = COLUMN_GROUPS.map((g) => g.id),
  onReorderGroups,
  onUnderlyingUpdated,
}) => {
  // Admin state
  const { isAdmin, updateUnderlyingTicker } = useAdmin();
  const [sorting, setSorting] = useState<SortingState>([]);
  // Initialize grouping based on prop
  const [grouping, setGrouping] = useState<GroupingState>(
    groupByUnderlying ? ['underlyingTicker'] : []
  );

  // Sort data so "No Underlying" groups are last, and create a lookup map
  const { sortedData, dataByTicker } = useMemo(() => {
    const lookup = new Map<string, TickerResult>();
    data.forEach(item => lookup.set(item.ticker, item));

    if (!groupByUnderlying) {
      return { sortedData: data, dataByTicker: lookup };
    }

    // Sort: items with underlying first (alphabetically), items without underlying last
    const sorted = [...data].sort((a, b) => {
      const aUnderlying = a.underlyingTicker || '';
      const bUnderlying = b.underlyingTicker || '';

      // Empty underlying goes to the end
      if (!aUnderlying && bUnderlying) return 1;
      if (aUnderlying && !bUnderlying) return -1;

      // Both have underlying or both don't - sort alphabetically
      return aUnderlying.localeCompare(bUnderlying);
    });

    return { sortedData: sorted, dataByTicker: lookup };
  }, [data, groupByUnderlying]);

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor)
  );

  // Update grouping state when prop changes
  useEffect(() => {
    setGrouping(groupByUnderlying ? ['underlyingTicker'] : []);
  }, [groupByUnderlying]);

  // Get ordered groups that have visible columns
  const orderedGroups = useMemo(
    () => getOrderedGroups(groupOrder, visibleColumns),
    [groupOrder, visibleColumns]
  );

  // Build ordered column list based on group order
  const orderedColumnList = useMemo(
    () => buildOrderedColumnList(groupOrder, visibleColumns),
    [groupOrder, visibleColumns]
  );

  const columns = useMemo<ColumnDef<TickerResult, any>[]>(
    () => {
      const allColumns: Record<string, ColumnDef<TickerResult, any>> = {
        ticker: columnHelper.accessor('ticker', {
          id: 'ticker',
          header: () => 'Ticker',
          cell: (info) => (
            <span className="font-semibold text-foreground">{info.getValue()}</span>
          ),
          sortingFn: 'alphanumeric',
        }),
        totalReturn: columnHelper.accessor('totalReturn', {
          id: 'totalReturn',
          header: () => 'Total Return',
          cell: (info) => {
            const value = info.getValue();
            const colorClass =
              value >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
            return (
              <span className={`font-medium ${colorClass}`}>
                {formatPercent(value)}
              </span>
            );
          },
          sortingFn: 'basic',
        }),
        cagr: columnHelper.accessor('cagr', {
          id: 'cagr',
          header: () => 'CAGR',
          cell: (info) => {
            const value = info.getValue();
            const colorClass =
              value >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
            return (
              <span className={`font-medium ${colorClass}`}>
                {formatPercent(value)}
              </span>
            );
          },
          sortingFn: 'basic',
        }),
        finalValue: columnHelper.accessor('finalValue', {
          id: 'finalValue',
          header: () => 'Final (100% DRIP)',
          cell: (info) => (
            <span className="text-muted-foreground">{formatCurrency(info.getValue())}</span>
          ),
          sortingFn: 'basic',
        }),
        dividendsReceived: columnHelper.accessor('dividendsReceived', {
          id: 'dividendsReceived',
          header: () => 'Dividends',
          cell: (info) => (
            <span className="text-muted-foreground">{formatCurrency(info.getValue())}</span>
          ),
          sortingFn: 'basic',
        }),
        inceptionDate: columnHelper.accessor('inceptionDate', {
          id: 'inceptionDate',
          header: () => 'Inception Date',
          cell: (info) => (
            <span className="text-muted-foreground">{formatDate(info.getValue())}</span>
          ),
          sortingFn: 'basic',
        }),
        finalValue0Percent: columnHelper.accessor('finalValue0Percent', {
          id: 'finalValue0Percent',
          header: () => 'Final (0% DRIP)',
          cell: (info) => (
            <span className="text-muted-foreground">{formatCurrency(info.getValue())}</span>
          ),
          sortingFn: 'basic',
        }),
        finalValue50Percent: columnHelper.accessor('finalValue50Percent', {
          id: 'finalValue50Percent',
          header: () => 'Final (50% DRIP)',
          cell: (info) => (
            <span className="text-muted-foreground">{formatCurrency(info.getValue())}</span>
          ),
          sortingFn: 'basic',
        }),
        finalValue100Percent: columnHelper.accessor('finalValue100Percent', {
          id: 'finalValue100Percent',
          header: () => 'Final (100% DRIP)',
          cell: (info) => (
            <span className="text-muted-foreground">{formatCurrency(info.getValue())}</span>
          ),
          sortingFn: 'basic',
        }),
        flatThresholdPercent: columnHelper.accessor('flatThresholdPercent', {
          id: 'flatThresholdPercent',
          header: () => 'Flat Threshold',
          cell: (info) => (
            <span className="text-muted-foreground">{formatPercent(info.getValue(), false)}</span>
          ),
          sortingFn: 'basic',
        }),
        dividendYield: columnHelper.accessor('dividendYield', {
          id: 'dividendYield',
          header: () => 'Div Yield',
          cell: (info) => (
            <span className="text-muted-foreground">{formatPercent(info.getValue(), false)}</span>
          ),
          sortingFn: 'basic',
        }),
        maxDrawdownPercent: columnHelper.accessor('maxDrawdownPercent', {
          id: 'maxDrawdownPercent',
          header: () => 'Max Drawdown',
          cell: (info) => {
            const value = info.getValue();
            return (
              <span className="text-red-600 dark:text-red-400">
                {value != null ? `-${Math.abs(value).toFixed(1)}%` : '-'}
              </span>
            );
          },
          sortingFn: 'basic',
        }),
        volatilityPercent: columnHelper.accessor('volatilityPercent', {
          id: 'volatilityPercent',
          header: () => 'Volatility',
          cell: (info) => (
            <span className="text-muted-foreground">{formatPercent(info.getValue(), false)}</span>
          ),
          sortingFn: 'basic',
        }),
        dividendGrowthRatePercent: columnHelper.accessor('dividendGrowthRatePercent', {
          id: 'dividendGrowthRatePercent',
          header: () => 'Div Growth',
          cell: (info) => {
            const value = info.getValue();
            if (value == null) return <span className="text-muted-foreground">-</span>;
            const colorClass =
              value >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
            return (
              <span className={colorClass}>{formatPercent(value)}</span>
            );
          },
          sortingFn: 'basic',
        }),
        underlyingTicker: columnHelper.accessor('underlyingTicker', {
          id: 'underlyingTicker',
          header: () => 'Underlying',
          cell: (info) => {
            const value = info.getValue();
            const symbol = info.row.original.ticker;
            return (
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground font-mono">{value || '-'}</span>
                {isAdmin && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditUnderlying(symbol, value ?? null);
                    }}
                    className="opacity-50 hover:opacity-100 p-0.5 rounded hover:bg-accent transition-all"
                    title={`Edit underlying for ${symbol}`}
                    aria-label={`Edit underlying for ${symbol}`}
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                )}
              </div>
            );
          },
          sortingFn: 'alphanumeric',
        }),
        startPrice: columnHelper.accessor('startPrice', {
          id: 'startPrice',
          header: () => 'Start Price',
          cell: (info) => (
            <span className="text-muted-foreground">{formatCurrency(info.getValue())}</span>
          ),
          sortingFn: 'basic',
        }),
        endPrice: columnHelper.accessor('endPrice', {
          id: 'endPrice',
          header: () => 'End Price',
          cell: (info) => (
            <span className="text-muted-foreground">{formatCurrency(info.getValue())}</span>
          ),
          sortingFn: 'basic',
        }),
        priceChangeDollars: columnHelper.accessor('priceChangeDollars', {
          id: 'priceChangeDollars',
          header: () => 'Price Δ ($)',
          cell: (info) => {
            const value = info.getValue();
            if (value == null) return <span className="text-muted-foreground">-</span>;
            const colorClass =
              value >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
            const sign = value >= 0 ? '+' : '';
            return (
              <span className={colorClass}>{sign}{formatCurrency(value).replace('$', '$')}</span>
            );
          },
          sortingFn: 'basic',
        }),
        priceChangePercent: columnHelper.accessor('priceChangePercent', {
          id: 'priceChangePercent',
          header: () => 'Price Δ (%)',
          cell: (info) => {
            const value = info.getValue();
            if (value == null) return <span className="text-muted-foreground">-</span>;
            const colorClass =
              value >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
            return (
              <span className={colorClass}>{formatPercent(value)}</span>
            );
          },
          sortingFn: 'basic',
        }),
      };

      // Filter and order columns based on group order and visibility
      const filteredColumns: ColumnDef<TickerResult, any>[] = orderedColumnList
        .filter(colId => allColumns[colId])
        .map(colId => allColumns[colId]);

      // Add Total Real Returns link column (always visible)
      filteredColumns.push(
        columnHelper.display({
          id: 'externalLink',
          header: () => <span className="sr-only">Total Real Returns</span>,
          cell: (info) => (
            <a
              href={`https://totalrealreturns.com/s/${info.row.original.ticker}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex justify-center items-center p-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title={`View ${info.row.original.ticker} on Total Real Returns`}
              aria-label={`View ${info.row.original.ticker} on Total Real Returns`}
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          ),
        })
      );

      // Add chart icon column if onRowClick is provided
      if (onRowClick) {
        filteredColumns.push(
          columnHelper.display({
            id: 'chartIcon',
            header: () => <span className="sr-only">View Chart</span>,
            cell: () => (
              <span className="flex justify-center">
                <LineChart className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              </span>
            ),
          })
        );
      }

      // Add remove button column if onRemoveTicker is provided
      if (onRemoveTicker) {
        filteredColumns.push(
          columnHelper.display({
            id: 'removeAction',
            header: () => <span className="sr-only">Remove</span>,
            cell: (info) => (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveTicker(info.row.original.ticker);
                }}
                className="flex justify-center items-center p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors"
                title={`Remove ${info.row.original.ticker}`}
                aria-label={`Remove ${info.row.original.ticker}`}
              >
                <X className="h-4 w-4" />
              </button>
            ),
          })
        );
      }

      return filteredColumns;
    },
    [onRowClick, onRemoveTicker, orderedColumnList, isAdmin]
  );

  const table = useReactTable({
    data: sortedData,
    columns,
    // Stable row ID based on ticker
    getRowId: (originalRow) => originalRow.ticker,
    state: {
      sorting,
      grouping,
    },
    // Use initialState for expanded - true means all groups expanded by default
    initialState: {
      expanded: true,
    },
    onSortingChange: setSorting,
    onGroupingChange: setGrouping,
    // Prevent TanStack from auto-resetting expanded state
    autoResetExpanded: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const handleRowClick = (ticker: string) => {
    if (onRowClick) {
      onRowClick(ticker);
    }
  };

  const handleRowKeyDown = (e: React.KeyboardEvent, ticker: string) => {
    if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onRowClick(ticker);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id && onReorderGroups) {
      onReorderGroups(active.id as string, over.id as string);
    }
  };

  // Admin: Handle editing underlying ticker
  const handleEditUnderlying = async (symbol: string, currentValue: string | null) => {
    const newValue = prompt('Enter underlying ticker (leave empty to clear):', currentValue || '');
    if (newValue === null) return; // User cancelled

    try {
      const trimmed = newValue.trim() || null;
      await updateUnderlyingTicker(symbol, trimmed);
      // Notify parent to refresh data
      if (onUnderlyingUpdated) {
        onUnderlyingUpdated(symbol, trimmed);
      }
    } catch (error) {
      console.error('Failed to update underlying ticker:', error);
      alert('Failed to update underlying ticker. Please try again.');
    }
  };

  // Show empty state only if no data AND no loading tickers
  if ((!sortedData || sortedData.length === 0) && loadingTickers.length === 0) {
    return (
      <div
        className={`bg-muted rounded-lg border border-border p-8 text-center ${className}`}
        role="status"
        aria-label="No data available"
      >
        <p className="text-muted-foreground text-lg">No data available to display</p>
      </div>
    );
  }

  // Get the number of visible columns for the loading row colspan
  // +1 for externalLink (always present), +1 for chartIcon (if onRowClick), +1 for removeAction (if onRemoveTicker)
  const visibleColumnCount = orderedColumnList.length + 1 + (onRowClick ? 1 : 0) + (onRemoveTicker ? 1 : 0);

  // Count action columns (externalLink is always present)
  const actionColumnCount = 1 + (onRowClick ? 1 : 0) + (onRemoveTicker ? 1 : 0);

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <div
        className={`w-full rounded-lg border border-border shadow-sm overflow-hidden ${className}`}
      >
        <table
          className="w-full table-fixed divide-y divide-border"
          role="table"
          aria-label="Returns summary table"
        >
          <thead>
            {/* Group header row - draggable */}
            <SortableContext items={orderedGroups.map((g) => g.id)} strategy={horizontalListSortingStrategy}>
              <tr className="border-b border-slate-600">
                {orderedGroups.map((group, idx) => {
                  const visibleInGroup = getVisibleColumnsInGroup(group, visibleColumns);
                  return (
                    <DraggableGroupHeader
                      key={group.id}
                      group={group}
                      colSpan={visibleInGroup.length}
                      isFirst={idx === 0}
                    />
                  );
                })}
                {/* Empty header for action columns */}
                {actionColumnCount > 0 && (
                  <th
                    colSpan={actionColumnCount}
                    className="bg-muted px-3 py-2 border-l-2 border-slate-500"
                  >
                    <span className="sr-only">Actions</span>
                  </th>
                )}
              </tr>
            </SortableContext>

            {/* Column header row - sortable */}
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-muted">
                {headerGroup.headers.map((header) => {
                  const columnId = header.column.id;
                  const group = getColumnGroup(columnId);
                  const isFirstInGroup = isFirstColumnInGroup(columnId, visibleColumns);
                  const isActionColumn = columnId === 'chartIcon' || columnId === 'removeAction';

                  return (
                    <th
                      key={header.id}
                      scope="col"
                      className={`px-2 py-2 text-left text-xs font-semibold text-foreground truncate
                        ${group ? group.headerColorClass : 'bg-muted'}
                        ${(isFirstInGroup || (isActionColumn && !group)) ? 'border-l-2 border-slate-500' : ''}
                        ${header.column.getCanSort() ? 'cursor-pointer select-none hover:bg-accent/50 transition-colors' : ''}
                      `}
                      onClick={header.column.getToggleSortingHandler()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          header.column.getToggleSortingHandler()?.(e);
                        }
                      }}
                      tabIndex={header.column.getCanSort() ? 0 : undefined}
                      aria-sort={
                        header.column.getIsSorted()
                          ? header.column.getIsSorted() === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                    >
                      <div className="flex items-center gap-2">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        {header.column.getCanSort() && (
                          <span className="text-muted-foreground">
                            {{
                              asc: ' ▲',
                              desc: ' ▼',
                            }[header.column.getIsSorted() as string] ?? ' ↕'}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-border bg-card">
            {table.getRowModel().rows.map((row, index) => {
              // Check if this is a grouped row (group header)
              if (row.getIsGrouped()) {
                const groupValue = row.getValue('underlyingTicker') as string;
                // Get the underlying ticker's data to display in the header row
                const underlyingData = groupValue ? dataByTicker.get(groupValue) : null;
                // Count child tickers (excluding the underlying itself)
                const childCount = row.subRows.filter(
                  subRow => subRow.original.ticker !== groupValue
                ).length;

                return (
                  <tr
                    key={row.id}
                    className="bg-muted/70 hover:bg-muted transition-colors"
                    aria-expanded={row.getIsExpanded()}
                    aria-label={`${row.getIsExpanded() ? 'Collapse' : 'Expand'} ${groupValue || 'Ungrouped'} group`}
                  >
                    {/* Render each column with underlying's data */}
                    {orderedColumnList.map((columnId, colIndex) => {
                      const isFirstInGroup = isFirstColumnInGroup(columnId, visibleColumns);
                      const isFirstColumn = colIndex === 0;

                      // First column shows expand/collapse + ticker + count
                      if (isFirstColumn) {
                        return (
                          <td
                            key={columnId}
                            className={`truncate px-2 py-2 text-sm cursor-pointer
                              ${isFirstInGroup ? 'border-l-2 border-slate-600' : ''}
                            `}
                            onClick={() => row.toggleExpanded()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                row.toggleExpanded();
                              }
                            }}
                            tabIndex={0}
                            role="button"
                          >
                            <div className="flex items-center gap-2 font-semibold text-foreground">
                              {row.getIsExpanded() ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              )}
                              <span className="font-mono">
                                {groupValue || 'No Underlying'}
                              </span>
                              <span className="text-muted-foreground font-normal text-sm">
                                ({childCount} {childCount === 1 ? 'ticker' : 'tickers'})
                              </span>
                            </div>
                          </td>
                        );
                      }

                      // Other columns show underlying's metrics (if available)
                      const value = underlyingData ? (underlyingData as any)[columnId] : null;

                      return (
                        <td
                          key={columnId}
                          className={`truncate px-2 py-2 text-sm
                            ${isFirstInGroup ? 'border-l-2 border-slate-600' : ''}
                          `}
                        >
                          {renderCellValue(columnId, value)}
                        </td>
                      );
                    })}
                    {/* Action columns for group header */}
                    {/* External link column (always present) */}
                    {groupValue ? (
                      <td className="px-2 py-2 text-sm border-l-2 border-slate-600">
                        <a
                          href={`https://totalrealreturns.com/s/${groupValue}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex justify-center items-center p-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                          title={`View ${groupValue} on Total Real Returns`}
                          aria-label={`View ${groupValue} on Total Real Returns`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </td>
                    ) : (
                      <td className="px-2 py-2 text-sm border-l-2 border-slate-600" />
                    )}
                    {onRowClick && underlyingData && (
                      <td
                        className="px-2 py-2 text-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowClick(groupValue);
                        }}
                      >
                        <span className="flex justify-center cursor-pointer">
                          <LineChart className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                        </span>
                      </td>
                    )}
                    {onRowClick && !underlyingData && (
                      <td className="px-2 py-2 text-sm" />
                    )}
                    {onRemoveTicker && (
                      <td className="px-2 py-2 text-sm" />
                    )}
                  </tr>
                );
              }

              // Regular data row - skip if this is the underlying ticker (shown in header)
              if (groupByUnderlying && row.original.ticker === row.original.underlyingTicker) {
                return null;
              }

              const isClickable = !!onRowClick;
              // When grouped, indent the rows slightly
              const isGrouped = groupByUnderlying && row.depth > 0;
              return (
                <tr
                  key={row.id}
                  className={`${
                    index % 2 === 0 ? 'bg-card' : 'bg-muted/30'
                  } ${
                    isClickable
                      ? 'cursor-pointer hover:bg-accent transition-colors'
                      : 'hover:bg-accent/50 transition-colors'
                  }`}
                  onClick={() => handleRowClick(row.original.ticker)}
                  onKeyDown={(e) => handleRowKeyDown(e, row.original.ticker)}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  aria-label={isClickable ? `View chart for ${row.original.ticker}` : undefined}
                >
                  {row.getVisibleCells().map((cell) => {
                    const columnId = cell.column.id;
                    const isFirstInGroup = isFirstColumnInGroup(columnId, visibleColumns);
                    // externalLink is now the first action column (always present)
                    const isFirstActionCol = columnId === 'externalLink';

                    return (
                      <td
                        key={cell.id}
                        className={`truncate px-2 py-2 text-sm
                          ${isFirstInGroup ? 'border-l-2 border-slate-600' : ''}
                          ${isFirstActionCol ? 'border-l-2 border-slate-600' : ''}
                          ${isGrouped && columnId === orderedColumnList[0] ? 'pl-6' : ''}
                        `}
                      >
                        {cell.getIsGrouped() ? (
                          // For grouped cells, show the value
                          flexRender(cell.column.columnDef.cell, cell.getContext())
                        ) : cell.getIsAggregated() ? (
                          // For aggregated cells, show aggregated value
                          flexRender(cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell, cell.getContext())
                        ) : cell.getIsPlaceholder() ? null : (
                          // Regular cell
                          flexRender(cell.column.columnDef.cell, cell.getContext())
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {/* Loading rows for tickers being calculated */}
            {loadingTickers.map((loadingTicker, index) => {
              const rowIndex = sortedData.length + index;
              return (
                <tr
                  key={`loading-${loadingTicker.symbol}`}
                  className={`${
                    rowIndex % 2 === 0 ? 'bg-card' : 'bg-muted/30'
                  } animate-pulse`}
                >
                  <td className="truncate px-2 py-2 text-sm">
                    <span className="font-semibold text-foreground">{loadingTicker.symbol}</span>
                  </td>
                  <td colSpan={visibleColumnCount - 1} className="truncate px-2 py-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <svg
                        className="animate-spin h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span>Calculating...</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </DndContext>
  );
};

export default ReturnsTable;
